import { useMemo, useState } from "react";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { Badge } from "@/components/ui/badge";
import { useGetAnnouncements } from "@/features/announcements/api/get-announcements";
import { AddAnnouncementModal } from "@/features/announcements/components/add-announcement-modal";
import { AnnouncementRowActions } from "@/features/announcements/components/announcement-row-actions";
import { AnnouncementsTable } from "@/features/announcements/components/announcements-table";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { PublicAnnouncement } from "@classview/shared-types";
import { createColumnHelper } from "@tanstack/react-table";

export function AnnouncementsPage() {
  const columnHelper = createColumnHelper<PublicAnnouncement>();
  const { data, isLoading, isSuccess, isError, error } = useGetAnnouncements();
  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.type.toString(), {
        id: "type",
        cell: (info) => {
          switch (info.getValue()) {
            case "ANNOUNCEMENT":
              return <Badge variant="info">Announcement</Badge>;
            case "URGENT":
              return <Badge variant="destructive">Urgent</Badge>;
            case "WARNING":
              return <Badge variant="warning">Warning</Badge>;
            case "NOTICE":
              return <Badge variant="primary">Notice</Badge>;
            default:
              return null;
          }
        },
        header: () => <span>Type</span>,
        size: 120,
      }),
      columnHelper.accessor("title", {
        id: "title",
        cell: (info) => {
          return info.getValue();
        },
        header: () => <span>Title</span>,
        size: 250,
      }),
      columnHelper.accessor("message", {
        id: "message",
        cell: (info) => {
          return info.getValue();
        },
        header: () => <span>Message</span>,
      }),

      columnHelper.display({
        id: "actions",
        cell: (info) => (
          <AnnouncementRowActions announcementId={info.row.original.id} />
        ),
        size: 70,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [search, setSearch] = useState<string | null>(null);

  const filteredData = useMemo(() => {
    if (!data) return [];

    return data.filter((announcement) =>
      announcement.title.toLowerCase().includes(search?.toLowerCase() ?? ""),
    );
  }, [data, search]);

  return (
    <div>
      <OnboardingHeader
        title="Announcements"
        description="Define platform wide messages"
        className="mb-6"
      />

      {isLoading && <LoadingScreen />}
      {isSuccess && (
        <>
          <div className="mb-2 flex flex-col justify-between gap-2 sm:flex-row">
            <SearchBar search={search} setSearch={setSearch} />
            <AddAnnouncementModal />
          </div>
          <AnnouncementsTable columns={columns} data={filteredData} />
        </>
      )}
      {isError && <ErrorMessage error={error} />}
    </div>
  );
}
